import { AdRequestSourceSpace } from './AdRequestSource';
import {
  AdRequestSourceWebService,
  OutdoorSpaceAttributePath,
  RestfulAdRequestSourceWebService
} from 'ws/AdRequestSourceWebService';
import {
  generateI18nOfSelectOptions,
  selectOptionsAddI18n
} from 'utils/I18nUtils';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { SpaceStatus } from 'enum/SpaceStatus';
import _ from 'lodash';

export const storeInfoExtraGetter = (record: any): string | undefined => {
  if (record.municipality == null || record.municipality.trim() === '') {
    return undefined;
  }
  if (record.storeNum == null || record.storeNum.trim() === '') {
    return undefined;
  }
  return `${record.municipality}/${record.storeNum}`;
};

export const deviceCharacteristicInfoExtraGetter = (
  record: any
): string | undefined => {
  if (record.municipality == null || record.municipality.trim() === '') {
    return undefined;
  }
  if (record.storeNum == null || record.storeNum.trim() === '') {
    return undefined;
  }
  if (
    record.deviceCharacteristic == null ||
    record.deviceCharacteristic.trim() === ''
  ) {
    return undefined;
  }
  return `${record.municipality}/${record.storeNum}/${record.deviceCharacteristic}`;
};

/**
 * Sort options by label asc, but push entries whose label contains "Others"
 * to the end, recursively. UX rule: dropdowns are easier to scan when the
 * catch-all "Others" bucket lives at the bottom regardless of locale (server
 * uses the literal "Others" marker for unresolvable groupings). Uses
 * `includes` rather than `startsWith` to also catch composed labels like
 * "Taipei City > Others" used by hierarchical attributes (e.g.
 * administrative-areas children).
 */
function sortWithOthersLast(opts: SelectOptions[]): SelectOptions[] {
  const isOthers = (o: SelectOptions) => String(o.label).includes('Others');
  const recurse = (list: SelectOptions[]) =>
    list.map((o) =>
      Array.isArray(o.options)
        ? { ...o, options: sortWithOthersLast(o.options) }
        : o
    );
  const [others, named] = _.partition(opts, isOthers);
  return [
    ..._.sortBy(recurse(named), ['label']),
    ..._.sortBy(recurse(others), ['label'])
  ];
}

export interface AdRequestSourceManager {
  getCountries(): Promise<SelectOptions[]>;
  getAdx(): Promise<SelectOptions[]>;
  getSegmentLabel(): Promise<SelectOptions[]>;
  getSSPSpaces(publisherIds?: string[]): Promise<AdRequestSourceSpace[]>;
  getSSPSpacesByAdType(
    adType: string,
    channel?: string,
    strict?: boolean
  ): Promise<AdRequestSourceSpace[]>;
  getAsiaMaxCountries(): Promise<SelectOptions[]>;
  getAges(): Promise<SelectOptions[]>;
  getGenders(): Promise<SelectOptions[]>;
  getOS(): Promise<SelectOptions[]>;
  getSpaceTypes(): Promise<SelectOptions[]>;
  getCarriers(): Promise<SelectOptions[]>;
  getDevice(): Promise<SelectOptions[]>;
  getTenmaxCategories(): Promise<SelectOptions[]>;
  getArticleCategories(): Promise<SelectOptions[]>;
  getContentLanguages(): Promise<SelectOptions[]>;
  getSegments(advertiserId: number): Promise<SelectOptions[]>;
  getTenmaxSegments(): Promise<SelectOptions[]>;
  getDomains(channel?: string): Promise<SelectOptions[]>;
  getAgencySegments(agencyId?: number): Promise<SelectOptions[]>;
  getSpaceAdFormats(): Promise<SelectOptions[]>;
  getSpaceAttributes(
    adFormat: string,
    channelGroupSetting?: ChannelGroupSetting
  ): Promise<{ [key: string]: SelectOptions[] }>;
  getOutdoorSpaceAttributes(
    channelGroupId: number,
    testMode?: boolean,
    /** 要涵蓋哪些版位狀態；不給就維持後端原本的範圍（只有啟用中的版位）。 */
    spaceStatuses?: SpaceStatus[],
    /** 只要這幾組屬性（用回傳 bag 的 key 指定）；不給就五組全取。 */
    attributeNames?: string[]
  ): Promise<{ [key: string]: SelectOptions[] }>;
  getSpacesWithConditions(
    valuePrefix: boolean,
    conditions: Record<string, string[]>,
    adFormat: string,
    channelGroupId?: number,
    extraGetter?: (record: any) => string | undefined,
    testMode?: boolean,
    deliverableOnly?: boolean
  ): Promise<SelectOptions[]>;
  getSpacesGroupsOfAdType(
    adType: string,
    adTypeId?: number,
    strict?: boolean
  ): Promise<SelectOptions[]>;
  getStoreNumOptions(
    channelGroupId: number,
    beaconConnectionId: number
  ): Promise<SelectOptions[]>;
}

export class DefaultAdRequestSourceManager implements AdRequestSourceManager {
  webService: AdRequestSourceWebService;

  constructor(
    webService: AdRequestSourceWebService = new RestfulAdRequestSourceWebService()
  ) {
    this.webService = webService;
  }
  async getCountries(): Promise<SelectOptions[]> {
    const countries = await this.webService.getCountries();
    return countries.map((country) =>
      selectOptionsAddI18n(country, ['geography.country.', 'geography.region.'])
    );
  }
  getAdx(): Promise<SelectOptions[]> {
    return this.webService.getAdx();
  }
  getSegmentLabel(): Promise<SelectOptions[]> {
    return this.webService.getSegmentLabel();
  }
  getSSPSpaces(publisherIds?: string[]): Promise<AdRequestSourceSpace[]> {
    const requestParameter =
      publisherIds && publisherIds.length > 0 ? publisherIds.join(',') : '';
    return this.webService.getSSPSpaces(requestParameter);
  }
  getSSPSpacesByAdType(
    adType: string,
    channel?: string,
    strict?: boolean
  ): Promise<AdRequestSourceSpace[]> {
    return this.webService.getSSPSpacesByAdType(adType, channel, strict);
  }
  getAges(): Promise<SelectOptions[]> {
    return this.webService.getAges();
  }
  getGenders(): Promise<SelectOptions[]> {
    return this.webService.getGenders();
  }
  getOS(): Promise<SelectOptions[]> {
    return this.webService.getOS();
  }
  getSpaceTypes(): Promise<SelectOptions[]> {
    return this.webService.getSpaceTypes();
  }
  getCarriers(): Promise<SelectOptions[]> {
    return this.webService.getCarriers();
  }
  getDevice(): Promise<SelectOptions[]> {
    return this.webService.getDevice();
  }
  getTenmaxCategories(): Promise<SelectOptions[]> {
    return this.webService.getTenmaxCategories();
  }
  getArticleCategories(): Promise<SelectOptions[]> {
    return this.webService.getArticleCategories();
  }
  getContentLanguages(): Promise<SelectOptions[]> {
    return this.webService.getContentLanguages();
  }
  getSegments(advertiserId: number): Promise<SelectOptions[]> {
    return this.webService.getSegments(advertiserId);
  }
  getTenmaxSegments(): Promise<SelectOptions[]> {
    return this.webService.getTenmaxSegments();
  }
  getDomains(channel?: L1ObjectChannel): Promise<SelectOptions[]> {
    return this.webService.getDomains(channel);
  }
  async getAsiaMaxCountries(): Promise<SelectOptions[]> {
    const countries = await this.webService.getAsiaMaxCountries();
    return countries.map((country) =>
      selectOptionsAddI18n(country, ['geography.country.', 'geography.region.'])
    );
  }
  async getAgencySegments(agencyId?: number): Promise<SelectOptions[]> {
    if (!agencyId) return [];
    return this.webService.getAgencySegments(agencyId);
  }

  async getSpaceAdFormats(): Promise<SelectOptions[]> {
    return this.webService.getSpaceAdFormats();
  }

  async getSpaceAttributes(
    adFormat: string,
    channelGroupSetting?: ChannelGroupSetting
  ): Promise<{ [key: string]: SelectOptions[] }> {
    return this.webService.getSpaceAttributes(adFormat, channelGroupSetting);
  }

  async getOutdoorSpaceAttributes(
    channelGroupId: number,
    testMode = false,
    spaceStatuses: SpaceStatus[] = [],
    attributeNames?: string[]
  ): Promise<{ [key: string]: SelectOptions[] }> {
    const allPathToAttrName: Array<[OutdoorSpaceAttributePath, string]> = [
      ['place-categories', 'outdoorPlaceCategories'],
      ['regions', 'cityGroup'],
      ['store-options', 'storeNum'],
      ['administrative-areas', 'administrativeArea'],
      ['characteristic', 'deviceCharacteristic']
    ];
    // 一組屬性就是一次請求，所以指定了要哪幾組的呼叫端只會打那幾支。
    // 不指定時維持五組全取：既有呼叫端拿的是整個 bag。
    const pathToAttrName = attributeNames
      ? allPathToAttrName.filter(([, attrName]) =>
          attributeNames.includes(attrName)
        )
      : allPathToAttrName;
    const results = await Promise.all(
      pathToAttrName.map(([path]) =>
        this.webService.getOutdoorSpaceAttributes(
          path,
          channelGroupId,
          testMode,
          spaceStatuses
        )
      )
    );
    const bag = pathToAttrName.reduce<{ [key: string]: SelectOptions[] }>(
      (acc, [, attrName], i) => ({ ...acc, [attrName]: results[i] }),
      {}
    );
    for (const key of Object.keys(bag)) {
      bag[key] = sortWithOthersLast(bag[key]);
    }
    return bag;
  }

  async getSpacesWithConditions(
    valuePrefix: boolean,
    conditions: Record<string, string[]>,
    adFormat: string,
    channelGroupId?: number,
    extraGetter?: (record: any) => string | undefined,
    testMode = false,
    deliverableOnly = false
  ): Promise<SelectOptions[]> {
    let attributes = { ...conditions };
    if (adFormat && adFormat !== '') {
      attributes = {
        ...attributes,
        adFormat: [adFormat]
      };
    }
    return this.webService.getSpaceByAttributesAndChannel(
      valuePrefix,
      attributes,
      extraGetter,
      channelGroupId,
      testMode,
      deliverableOnly
    );
  }

  async getSpacesGroupsOfAdType(
    adType: string,
    adTypeId?: number,
    strict?: boolean
  ): Promise<SelectOptions[]> {
    return this.webService.getSpacesGroupsOfAdType(adType, adTypeId, strict);
  }

  async getStoreNumOptions(
    channelGroupId: number,
    beaconConnectionId: number
  ): Promise<SelectOptions[]> {
    const storeNumOptions = await this.webService.getStoreNumOptions(
      channelGroupId,
      beaconConnectionId
    );
    storeNumOptions.forEach((option) =>
      generateI18nOfSelectOptions(option, 'store')
    );
    return storeNumOptions;
  }
}
